<script setup>
import { useForm, usePage, router, Head, Link } from '@inertiajs/vue3';
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';
import ImageUpload from '@/Components/ImageUpload.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

const props = defineProps({
  news: Array,
  categories: Array,
  tags: Array,
  filters: Object,
  errors: Object,
  flash: Object
});

const page = usePage();
const isEditing = ref(false);
const modalItem = ref({});

// Form Data
const form = useForm({
  id: '',
  title: '',
  slug: '',
  category_id: '',
  sub_category_id: '', // Optional
  tags: [],
  content: '',
  excerpt: '',
  image: null,
  author: page.props.auth?.user?.name || '', // Default to current user
  is_published: false,
  published_at: '',
  meta_title: '',
  meta_description: '',
});

// CKEditor Instances
let contentEditor = null;
let excerptEditor = null;

// Initialize CKEditor
const initEditor = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    if (CKEDITOR.instances['news_content']) CKEDITOR.instances['news_content'].destroy();
    if (CKEDITOR.instances['excerpt']) CKEDITOR.instances['excerpt'].destroy();

    // Editor untuk konten utama
    contentEditor = CKEDITOR.replace('news_content', {
        height: 400,
        removeButtons: '',
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList', 'Blockquote'] },
            { name: 'links', items: ['Link', 'Unlink'] },
            { name: 'insert', items: ['HorizontalRule'] },
            { name: 'styles', items: ['Format'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ],
        warnOnUnload: false,
        dialog_noConfirmCancel: true
    });

    contentEditor.on('change', () => {
         form.content = contentEditor.getData();
    });

    // Editor untuk ringkasan artikel (simplified)
    excerptEditor = CKEDITOR.replace('excerpt', {
        height: 120,
        removeButtons: '',
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic'] },
            { name: 'undo', items: ['Undo', 'Redo'] }
        ],
        warnOnUnload: false,
        dialog_noConfirmCancel: true
    });

    excerptEditor.on('change', () => {
         form.excerpt = excerptEditor.getData();
    });
};

onMounted(() => {
    nextTick(() => {
        initEditor();
    });
    
    // Show flash error if present
    if (page.props.flash?.error) {
        errorModalContent.value = [page.props.flash.error];
        modalType.value = 'error';
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
    }
});

onBeforeUnmount(() => {
    if (contentEditor) {
        try { contentEditor.destroy(); } catch (e) { console.error(e); }
        contentEditor = null;
    }
    if (excerptEditor) {
         try { excerptEditor.destroy(); } catch (e) { console.error(e); }
         excerptEditor = null;
    }
    // Also try removing by ID just to be safe
    if (typeof CKEDITOR !== 'undefined') {
        if (CKEDITOR.instances['news_content']) try { CKEDITOR.instances['news_content'].destroy(); } catch(e) {}
        if (CKEDITOR.instances['excerpt']) try { CKEDITOR.instances['excerpt'].destroy(); } catch(e) {}
    }
});

// Helper functions
const slugify = (text) => {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

const getImageUrl = (path) => {
    if (!path) return null;
    return path.includes('/') ? `/storage/${path}` : `/storage/news/${path}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
};

// Auto-generate stuff
// Auto-generate stuff
watch(() => form.title, (newValue) => {
    if (!isEditing.value && newValue) {
         form.slug = slugify(newValue);
         if (!form.meta_title) form.meta_title = newValue.substring(0, 55);
         
         // Auto-generate meta_description from title if empty
         if (!form.meta_description) {
            form.meta_description = newValue.substring(0, 155);
         }
    }
});

// Actions
const errorModalContent = ref([]);
const modalType = ref('error');
const saving = ref(false);
const originalData = ref(null);
const imageUploadKey = ref(0);
const selectedTags = ref([]);

// Sync selectedTags (Objects) to form.tags (IDs)
watch(selectedTags, (newTags) => {
    form.tags = newTags.map(t => t.id);
}, { deep: true });

// Tag Management
const availableTags = ref([...props.tags]);
watch(() => props.tags, (newTags) => {
    availableTags.value = [...newTags];
});

const tagForm = ref({
    name: '',
    color: '#000000',
    description: ''
});
const tagErrors = ref({});
const isTagSubmitting = ref(false);
const isEditingTag = ref(false);
const editingTagId = ref(null);

// Helper to get random soft pastel color
const generateRandomColor = () => {
    // List of soft/pastel hex colors
    const colors = [
        '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', // Pastel Rainbow
        '#E6B3B3', '#E6C99C', '#E6E6B3', '#B3E6CC', '#B3CCE6', // Muted
        '#F0E6EF', '#E6F0EF', '#EFF0E6', '#F0EFE6', '#E6EFF0', // Very Light
        '#D4C1EC', '#9F9FB0', '#8D99AE', '#E0AFA0', '#F2D7D5'  // Others
    ];
    const randomIndex = Math.floor(Math.random() * colors.length);
    tagForm.value.color = colors[randomIndex];
};

const resetTagForm = () => {
    tagForm.value = { name: '', color: '', description: '' };
    generateRandomColor(); // Set initial random color
    tagErrors.value = {};
    isEditingTag.value = false;
    editingTagId.value = null;
};

const openTagModal = () => {
    resetTagForm();
    const modal = new bootstrap.Modal(document.getElementById('tagModal'));
    modal.show();
};

const editTag = (tag) => {
    tagForm.value = {
        name: tag.name,
        color: tag.color,
        description: tag.description || ''
    };
    isEditingTag.value = true;
    editingTagId.value = tag.id;
    tagErrors.value = {};
};

const submitTag = async () => {
    isTagSubmitting.value = true;
    tagErrors.value = {};
    
    try {
        let response;
        if (isEditingTag.value) {
            response = await axios.put(route('admin.tag.update', editingTagId.value), tagForm.value);
        } else {
            response = await axios.post(route('admin.tag.store'), tagForm.value);
        }
        
        if (response.data.status === 'success') {
            if (isEditingTag.value) {
                // Update existing tag in availableTags
                const index = availableTags.value.findIndex(t => t.id === editingTagId.value);
                if (index !== -1) {
                    availableTags.value[index] = response.data.data;
                }
                // Reset to create mode but keep modal open (or reset form)
                resetTagForm();
                // Optional: Show checkmark/success feedback
            } else {
                // Add new tag to availableTags
                availableTags.value.push(response.data.data);
                // Auto-select the new tag (Push Object not ID)
                selectedTags.value.push(response.data.data);
                // Reset form for next entry
                resetTagForm();
            }
        }
    } catch (error) {
        if (error.response && error.response.status === 422) {
             if (error.response.data.messages) {
                 tagErrors.value.general = error.response.data.messages.join(', ');
             } else {
                 tagErrors.value.general = 'Terjadi kesalahan validasi.';
             }
        } else {
            console.error(error);
            tagErrors.value.general = isEditingTag.value ? 'Gagal memperbarui tag.' : 'Gagal menyimpan tag.';
        }
    } finally {
        isTagSubmitting.value = false;
    }
};

const tagToDelete = ref(null);
const isDeletingTag = ref(false);
const deleteTagConfirmedCheck = ref(false);

const confirmDeleteTag = (tag) => {
    tagToDelete.value = tag;
    deleteTagConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteTagModal'));
    modal.show();
};

const destroyTag = async () => {
    if (!tagToDelete.value) return;
    
    isDeletingTag.value = true;
    try {
        const response = await axios.delete(route('admin.tag.destroy', tagToDelete.value.id));
        if (response.data.status === 'success') {
            // Remove from available tags
            availableTags.value = availableTags.value.filter(t => t.id !== tagToDelete.value.id);
            // Remove from selected tags if present
            selectedTags.value = selectedTags.value.filter(tag => tag.id !== tagToDelete.value.id);
            
            // Hide Delete Modal
            const modalEl = document.getElementById('deleteTagModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    } catch (error) {
        console.error(error);
        alert('Gagal menghapus tag. Tag mungkin sedang digunakan.');
    } finally {
        isDeletingTag.value = false;
    }
};

const resetForm = () => {
    isEditing.value = false;
    modalItem.value = {};
    form.reset();
    form.clearErrors();
    form.tags = [];
    selectedTags.value = [];
    if (contentEditor) contentEditor.setData('');
    if (excerptEditor) excerptEditor.setData('');
    
    // Force re-render ImageUpload to clear internal state
    imageUploadKey.value++;
};

const edit = (item) => {
    isEditing.value = true;
    modalItem.value = item;
    
    form.id = item.id;
    form.title = item.title;
    form.slug = item.slug;
    form.category_id = item.category_id;
    form.sub_category_id = item.sub_category_id;
    
    // Map tags to multiselect format
    // Ensure we use the objects from item.tags
    // We also need to make sure they exist in availableTags or just use them as is (track-by handles it)
    selectedTags.value = item.tags ? [...item.tags] : [];
    form.tags = selectedTags.value.map(t => t.id); // Init form.tags
    
    form.content = item.content || '';
    form.excerpt = item.excerpt || '';
    form.author = item.author;
    form.is_published = !!item.is_published;
    form.published_at = item.published_at ? item.published_at.substring(0, 16) : ''; // format for datetime-local
    
    form.meta_title = item.meta_title || '';
    form.meta_description = item.meta_description || '';
    
    form.image = null; // Don't preload file input

    if (contentEditor) {
        contentEditor.setData(item.content || '', function() {
            this.resetDirty();
        });
    }
    if (excerptEditor) {
        excerptEditor.setData(item.excerpt || '', function() {
            this.resetDirty();
        });
    }

    // Store original data for comparison
    originalData.value = JSON.parse(JSON.stringify({
        title: item.title,
        slug: item.slug || '',
        category_id: item.category_id,
        sub_category_id: item.sub_category_id,
        tags: item.tags ? item.tags.map(tag => tag.id).sort() : [],
        author: item.author || '',
        is_published: !!item.is_published,
        published_at: item.published_at ? item.published_at.substring(0, 16) : '',
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || ''
    }));
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const submit = async () => {
    // Sync CKEditor data
    if (contentEditor) form.content = contentEditor.getData();
    if (excerptEditor) form.excerpt = excerptEditor.getData();
    
    saving.value = true;

    // Short delay for spinner visibility (like JenisMobil)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Client-side Validation
    const errors = [];
    if (!form.title || form.title.trim() === '') {
        errors.push('Judul artikel wajib diisi.');
    }
    if (!form.category_id) {
        errors.push('Kategori wajib dipilih.');
    }
    if (!form.content || form.content.trim() === '') {
        errors.push('Konten artikel wajib diisi.');
    }
    // Image validation: Required only on Create
    if (!isEditing.value && !form.image) {
        errors.push('Gambar utama wajib diupload untuk artikel baru.');
    }

    if (errors.length > 0) {
        saving.value = false;
        errorModalContent.value = errors;
        modalType.value = 'error';
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
        return;
    }

    const options = {
        preserveScroll: true,
        forceFormData: true,
        onSuccess: () => {
            saving.value = false;
            resetForm();
        },
        onError: (errors) => {
            saving.value = false;
            errorModalContent.value = Object.values(errors).flat();
            modalType.value = 'error';
            const modal = new bootstrap.Modal(document.getElementById('errorModal'));
            modal.show();
        },
        onFinish: () => {
            saving.value = false;
        }
    };

    if (isEditing.value) {
        // Validation for "No Change" logic
        if (originalData.value) {
            const currentData = {
                title: form.title,
                slug: form.slug || '',
                category_id: form.category_id,
                sub_category_id: form.sub_category_id,
                tags: [...form.tags].sort(),
                author: form.author || '',
                is_published: form.is_published,
                published_at: form.published_at || '',
                meta_title: form.meta_title || '',
                meta_description: form.meta_description || ''
            };

            const simpleFieldsMatch = JSON.stringify(originalData.value) === JSON.stringify(currentData);
            const contentDirty = contentEditor ? contentEditor.checkDirty() : false;
            const excerptDirty = excerptEditor ? excerptEditor.checkDirty() : false;
            const imageChanged = !!form.image;

            if (simpleFieldsMatch && !contentDirty && !excerptDirty && !imageChanged) {
                saving.value = false;
                errorModalContent.value = ['Tidak ada perubahan data yang disimpan.'];
                modalType.value = 'warning';
                const modal = new bootstrap.Modal(document.getElementById('errorModal'));
                modal.show();
                return;
            }
        }

        form.transform((data) => {
            const formData = {
                ...data,
                _method: 'PUT'
            };
            // If image is null (not changed), remove it so backend doesn't overwrite with null
            if (!data.image) delete formData.image;
            return formData;
        }).post(route('admin.news.update', form.id), options);
    } else {
        form.post(route('admin.news.store'), options);
    }
};

// Delete
const deleteForm = useForm({});
const deleteId = ref(null);
const deleteTitle = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteTitle.value = item.title;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = () => {
    if (!deleteId.value) return;
    deleteForm.delete(route('admin.news.destroy', deleteId.value), {
        preserveScroll: true,
        onSuccess: () => {
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    });
};

// Toggle Status
const processingStatusId = ref(null);
const toggleStatus = (item) => {
    processingStatusId.value = item.id;
    // Note: NewsController uses POST 'toggleStatus' with 'id' and 'is_published' in body
    // We can use router.post
    router.post(route('admin.news.toggleStatus'), {
        id: item.id,
        is_published: !item.is_published
    }, {
        preserveScroll: true,
        onFinish: () => processingStatusId.value = null
    });
};

// Filter/Search
const search = ref(props.filters?.search || '');
const filterCategory = ref(props.filters?.category || '');

watch(search, (value) => {
    router.get(route('admin.news.index'), { search: value, category: filterCategory.value }, {
        preserveState: true,
        replace: true,
    });
});

watch(filterCategory, (value) => {
    router.get(route('admin.news.index'), { search: search.value, category: value }, {
        preserveState: true,
        replace: true,
    });
});
</script>

<template>
    <Head title="Berita dan Artikel" />
    <div class="container-full">
        <!-- Breadcrumb Section -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Berita & Artikel</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Berita & Artikel</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Section -->
                <div class="col-12">
                     <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-plus me-2" v-if="!isEditing"></i>
                                <i class="ti-pencil me-2" v-else></i>
                                {{ isEditing ? 'Edit Artikel' : 'Buat Artikel Baru' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submit">
                            <div class="box-body">
                                <!-- Top Section: Image & Title/Slug -->
                                <div class="row mb-4">
                                    <div class="col-lg-4 col-md-5 col-12">
                                        <div class="form-group mb-3">
                                            <label class="form-label" for="file-upload-image">Gambar Utama</label>
                                            <ImageUpload 
                                                :key="imageUploadKey"
                                                label="" 
                                                field="image" 
                                                :current-url="getImageUrl(isEditing && !form.image ? modalItem.image : null)" 
                                                @update:file="file => form.image = file" 
                                                height="200px"
                                                help-text="Format: JPG, PNG, WEBP. Max: 2MB."
                                            />
                                            <div class="text-danger small mt-1" v-if="form.errors.image">{{ form.errors.image }}</div>
                                        </div>
                                    </div>
                                    <div class="col-lg-8 col-md-7 col-12">
                                        <div class="form-group mb-3">
                                            <label for="title" class="form-label">Judul Artikel <span class="text-danger">*</span></label>
                                            <input type="text" id="title" name="title" v-model="form.title" class="form-control" :class="{ 'is-invalid': form.errors.title }" placeholder="Masukkan judul artikel yang menarik..." autocomplete="off">
                                            <div class="invalid-feedback">{{ form.errors.title }}</div>
                                        </div>
                                        
                                        <div class="form-group mb-3">
                                            <label for="slug" class="form-label">Slug (URL)</label>
                                            <div class="input-group">
                                                <span class="input-group-text">{{ page.props.app_url }}/artikel/</span>
                                                <input type="text" id="slug" name="slug" v-model="form.slug" class="form-control" :class="{ 'is-invalid': form.errors.slug }" autocomplete="off">
                                            </div>
                                            <div class="invalid-feedback d-block" v-if="form.errors.slug">{{ form.errors.slug }}</div>
                                        </div>

                                        <div class="row">
                                            <!-- Left Column: Category & Tags -->
                                            <div class="col-md-6">
                                                <div class="form-group mb-3">
                                                    <label for="category_id" class="form-label">Kategori <span class="text-danger">*</span></label>
                                                    <select id="category_id" name="category_id" v-model="form.category_id" class="form-select form-control" :class="{ 'is-invalid': form.errors.category_id }" autocomplete="off">
                                                        <option value="">Pilih Kategori</option>
                                                        <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                                                    </select>
                                                    <div class="invalid-feedback">{{ form.errors.category_id }}</div>
                                                </div>

                                                <div class="form-group mb-0">
                                                    <label for="tags" class="form-label">Tags</label>
                                                    <div class="d-flex">
                                                        <div class="flex-grow-1">
                                                            <Multiselect
                                                                id="tags"
                                                                name="tags"
                                                                v-model="selectedTags"
                                                                :options="availableTags"
                                                                track-by="id"
                                                                label="name"
                                                                :multiple="true"
                                                                :close-on-select="false"
                                                                placeholder="Pilih Tags"
                                                            />
                                                        </div>
                                                        <button type="button" class="btn btn-outline-secondary" style="height: 38px; margin-left: -1px; border-radius: 0;" @click="openTagModal" title="Kelola Tags">
                                                            <i class="fa fa-cog"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Right Column: SEO Meta Data -->
                                            <div class="col-md-6">
                                                <div class="card bg-light-light mb-0">
                                                    <div class="card-body py-3">
                                                        <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                                        
                                                        <div class="form-group mb-2">
                                                            <div class="d-flex justify-content-between mb-1">
                                                                <label for="meta_title" class="form-label mb-0">Meta Title</label>
                                                                <small :class="form.meta_title?.length > 55 ? 'text-danger' : 'text-muted'" style="font-size: 11px;">{{ form.meta_title?.length || 0 }}/55</small>
                                                            </div>
                                                            <textarea id="meta_title" name="meta_title" v-model="form.meta_title" class="form-control" rows="3" maxlength="55" placeholder="Meta Title" autocomplete="off"></textarea>
                                                        </div>

                                                        <div class="form-group mb-0">
                                                            <div class="d-flex justify-content-between mb-1">
                                                                <label for="meta_description" class="form-label mb-0">Meta Description</label>
                                                                <small :class="form.meta_description?.length > 155 ? 'text-danger' : 'text-muted'" style="font-size: 11px;">{{ form.meta_description?.length || 0 }}/155</small>
                                                            </div>
                                                            <textarea id="meta_description" name="meta_description" v-model="form.meta_description" class="form-control" rows="5" maxlength="155" placeholder="Meta Description"></textarea>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div> <!-- End col-lg-8 -->
                                </div> <!-- End Top Row -->

                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-group mb-3">
                                            <label class="form-label" for="news_content">Konten Artikel <span class="text-danger">*</span></label>
                                            <textarea id="news_content" name="news_content" class="form-control" :class="{ 'is-invalid': form.errors.content }"></textarea>
                                            <div class="invalid-feedback d-block" v-if="form.errors.content">{{ form.errors.content }}</div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Bottom Row: Excerpt & Publish -->
                                <div class="row">
                                    <!-- Left: Excerpt -->
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="excerpt" class="form-label">Ringkasan (Excerpt)</label>
                                            <textarea id="excerpt" name="excerpt" class="form-control" rows="4" maxlength="300" :class="{ 'is-invalid': form.errors.excerpt }" placeholder="Ringkasan singkat untuk tampilan list (opsional)"></textarea>
                                            <div class="d-flex justify-content-between">
                                                 <div class="invalid-feedback d-block" v-if="form.errors.excerpt">{{ form.errors.excerpt }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right: Publish Settings -->
                                    <div class="col-md-6">
                                        <div class="card bg-light-light mb-0">
                                            <div class="card-body py-3">
                                                <h5 class="text-primary mb-3"><i class="ti-save me-2"></i>Publikasi</h5>
                                                
                                                <div class="form-group mb-2">
                                                    <label class="form-label" for="is_published">Status Artikel</label>
                                                    <div class="form-check form-switch custom-switch-lg ps-0 d-flex align-items-center">
                                                        <input class="form-check-input ms-0" type="checkbox" id="is_published" name="is_published" v-model="form.is_published">
                                                        <label class="form-check-label ms-2" for="is_published">
                                                            {{ form.is_published ? 'Diterbitkan (Published)' : 'Draft' }}
                                                        </label>
                                                    </div>
                                                </div>
                                                
                                                <div class="row">
                                                    <div class="col-md-6">
                                                        <div class="form-group mb-2">
                                                            <label for="published_at" class="form-label">Waktu Publikasi</label>
                                                            <input type="datetime-local" id="published_at" name="published_at" v-model="form.published_at" class="form-control" autocomplete="off">
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group mb-2">
                                                            <label for="author" class="form-label">Penulis</label>
                                                            <input type="text" id="author" name="author" v-model="form.author" class="form-control" autocomplete="off">
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="box-footer text-end">
                                <button type="button" class="btn btn-warning me-2" v-if="isEditing" @click="resetForm"><i class="ti-trash me-2"></i> Batal</button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                     </div>
                </div>
            </div>

            <!-- List Section -->
             <div class="row mt-4">
                <div class="col-12">
                     <div class="box">
                        <div class="box-header with-border">
                            <div class="d-flex justify-content-between align-items-center">
                                <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Artikel</h4>
                                <div class="d-flex gap-2">
                                    <select v-model="filterCategory" name="filter_category" aria-label="Filter Kategori" class="form-select form-select-sm" style="width: 150px;">
                                        <option value="">Semua Kategori</option>
                                        <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                                    </select>
                                    <input type="text" v-model="search" name="search" aria-label="Cari judul" class="form-control form-control-sm" placeholder="Cari judul..." style="width: 200px;">
                                </div>
                            </div>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th style="width: 50px;">No</th>
                                            <th>Gambar</th>
                                            <th>Judul / Info</th>
                                            <th>Kategori / Tags</th>
                                            <th>Status</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="news.length === 0">
                                            <td colspan="6" class="text-center py-4">Belum ada artikel.</td>
                                        </tr>
                                        <tr v-for="(item, index) in news" :key="item.id" class="align-middle">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <img :src="getImageUrl(item.image) || '/back/images/placeholder.jpg'" class="rounded" style="width: 60px; height: 40px; object-fit: cover;">
                                            </td>
                                            <td>
                                                <div class="fw-bold text-truncate" style="max-width: 300px;" :title="item.title">{{ item.title }}</div>
                                                <small class="text-muted">
                                                    <i class="fa fa-user me-1"></i>{{ item.author }} &bull; 
                                                    <i class="fa fa-clock-o me-1"></i>{{ formatDate(item.published_at) }}
                                                </small>
                                                <div class="small text-muted"><i class="fa fa-eye me-1"></i>{{ item.views || 0 }} views</div>
                                            </td>
                                            <td>
                                                <div class="badge bg-primary mb-1">{{ item.category_model?.name }}</div>
                                                <div v-if="item.tags && item.tags.length > 0">
                                                    <span v-for="tag in item.tags.slice(0, 2)" :key="tag.id" class="badge bg-light text-dark border me-1">{{ tag.name }}</span>
                                                    <span v-if="item.tags.length > 2" class="badge bg-light text-dark border">+{{ item.tags.length - 2 }}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <button type="button" class="btn btn-sm" :class="item.is_published ? 'btn-success' : 'btn-secondary'" @click="toggleStatus(item)" :disabled="processingStatusId === item.id">
                                                     <span v-if="processingStatusId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                     <i v-else class="fa me-1" :class="item.is_published ? 'fa-check' : 'fa-times'"></i>
                                                     {{ item.is_published ? 'Published' : 'Draft' }}
                                                </button>
                                            </td>
                                            <td class="text-center">
                                                <div class="btn-group">
                                                     <button class="btn btn-primary btn-sm" @click="edit(item)" title="Edit"><i class="fa fa-edit"></i></button>
                                                     <button class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                     </div>
                </div>
            </div>
        </section>

        <!-- Tag Modal -->
        <div class="modal fade" id="tagModal" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title"><i class="fa fa-tags me-2"></i>Kelola Tags</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body p-0">
                         <div class="row g-0">
                            <!-- Left: Form -->
                            <div class="col-md-5 border-end border-light">
                                <div class="p-4">
                                    <div class="d-flex justify-content-between align-items-center mb-3">
                                         <h6 class="text-primary mb-0">{{ isEditingTag ? 'Edit Tag' : 'Tambah Tag Baru' }}</h6>
                                         <button v-if="isEditingTag" @click="resetTagForm" class="btn btn-xs btn-outline-secondary" title="Batalkan Edit">
                                             <i class="fa fa-times"></i>
                                         </button>
                                    </div>
                                    
                                    <form @submit.prevent="submitTag">
                                        <div class="alert alert-danger p-2 mb-3" v-if="tagErrors.general">{{ tagErrors.general }}</div>
                                        <div class="mb-3">
                                            <label class="form-label" for="tag_name">Nama Tag <span class="text-danger">*</span></label>
                                            <input type="text" id="tag_name" name="tag_name" v-model="tagForm.name" class="form-control" autocomplete="off" required placeholder="Contoh: Tips, Info">
                                        </div>
                                        
                                        <!-- Auto-generated Color Preview -->
                                        <div class="mb-3">
                                            <div class="form-label d-block">Preview Tag</div>
                                            <div class="d-flex align-items-center gap-2">
                                                <span class="badge" :style="{ backgroundColor: tagForm.color, color: '#333', border: '1px solid #ddd' }">
                                                    {{ tagForm.name || 'Nama Tag' }}
                                                </span>
                                                <button type="button" class="btn btn-xs btn-outline-secondary" @click="generateRandomColor" title="Ganti Warna Acak">
                                                    <i class="fa fa-refresh"></i>
                                                </button>
                                            </div>
                                            <input type="hidden" v-model="tagForm.color">
                                        </div>

                                        <div class="mb-3">
                                            <label class="form-label" for="tag_description">Deskripsi</label>
                                            <textarea id="tag_description" name="tag_description" v-model="tagForm.description" class="form-control" rows="3" placeholder="Deskripsi singkat tag..."></textarea>
                                        </div>
                                        <div class="d-grid gap-2">
                                            <button type="submit" class="btn btn-primary" :disabled="isTagSubmitting">
                                                <span v-if="isTagSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="fa fa-save me-1"></i> {{ isEditingTag ? 'Update Tag' : 'Simpan Tag' }}
                                            </button>
                                            <button v-if="isEditingTag" type="button" class="btn btn-light border" @click="resetTagForm">Batal</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            
                            <!-- Right: List -->
                            <div class="col-md-7">
                                <div class="p-4">
                                     <h6 class="mb-3 text-primary d-flex justify-content-between align-items-center">
                                        <span>Daftar Tags Tersedia</span>
                                        <span class="badge bg-light text-dark">{{ availableTags.length }} Total</span>
                                     </h6>
                                     <div class="table-responsive" style="max-height: 350px; overflow-y: auto;">
                                         <table class="table table-hover table-sm">
                                             <thead class="table-light sticky-top">
                                                 <tr>
                                                     <th>Tag</th>
                                                     <th>Deskripsi</th>
                                                     <th class="text-center" style="width: 80px;">Aksi</th>
                                                 </tr>
                                             </thead>
                                             <tbody>
                                                 <tr v-if="availableTags.length === 0">
                                                     <td colspan="3" class="text-center py-3 text-muted">Belum ada tag</td>
                                                 </tr>
                                                 <tr v-for="tag in availableTags" :key="tag.id" :class="{ 'table-active': editingTagId === tag.id }">
                                                     <td>
                                                         <span class="badge" :style="{ backgroundColor: tag.color, color: '#333', border: '1px solid #ddd' }">{{ tag.name }}</span>
                                                     </td>
                                                     <td class="small text-muted text-truncate" style="max-width: 150px;" :title="tag.description">{{ tag.description || '-' }}</td>
                                                     <td class="text-center">
                                                         <div class="btn-group btn-group-sm">
                                                             <button @click="editTag(tag)" class="btn btn-xs btn-outline-primary" title="Edit Tag">
                                                                 <i class="fa fa-pencil"></i>
                                                             </button>
                                                             <button @click="confirmDeleteTag(tag)" class="btn btn-xs btn-outline-danger" title="Hapus Tag">
                                                                 <i class="fa fa-trash"></i>
                                                             </button>
                                                         </div>
                                                     </td>
                                                 </tr>
                                             </tbody>
                                         </table>
                                     </div>
                                </div>
                            </div>
                         </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tag Delete Confirmation Modal (Stacked) -->
        <div class="modal fade" id="deleteTagModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false" style="z-index: 1060;">
            <div class="modal-dialog modal-dialog-centered modal-sm">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="fa fa-trash me-2"></i>Hapus Tag</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus tag <strong>{{ tagToDelete?.name }}</strong>?</p>
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmTagDeleteCheck" v-model="deleteTagConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmTagDeleteCheck">
                                Saya yakin menghapus tag ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">Batal</button>
                        <button type="button" class="btn btn-danger btn-sm" :disabled="!deleteTagConfirmedCheck || isDeletingTag" @click="destroyTag">
                            <span v-if="isDeletingTag" class="spinner-border spinner-border-sm me-1"></span>
                            Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

         <!-- Delete Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                         <p>Apakah Anda yakin ingin menghapus artikel <strong>{{ deleteTitle }}</strong>?</p>
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">Hapus</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title" id="deleteModalLabel"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus artikel:</p>
                        <p class="fw-bold text-primary">"{{ deleteTitle }}"</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmDeleteCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmDeleteCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck || deleteForm.processing" @click="submitDelete">
                            <span v-if="deleteForm.processing" class="spinner-border spinner-border-sm me-1"></span>
                            <i v-else class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<style>
/* Overrides for Vue Multiselect to match Select2/Bootstrap Compactness */
/* Adjusted for Proportional Sizing (Not too small) */
.multiselect {
    min-height: 38px; /* Standard Height */
}
.multiselect__tags {
    min-height: 38px;
    padding: 4px 40px 0 8px; /* Reduced Top Padding */
    border: 1px solid #d2d6de;
    border-radius: 0;
    font-size: 14px;
}
.multiselect__tag {
    margin-bottom: 2px; /* Reduced Bottom Margin */
    margin-right: 4px;
    padding: 2px 24px 2px 8px; /* Slimmer pills */
    border-radius: 3px;
    background: #e4e4e4; 
    color: #333;
    border: 1px solid #d2d6de;
    font-size: 13px;
}
.multiselect__tag-icon {
    line-height: 20px;
    width: 20px;
}
.multiselect__tag-icon:after {
    color: #777;
    font-size: 14px;
}
.multiselect__tag-icon:hover {
    background: #d2d6de;
    color: #333;
}
.multiselect__placeholder {
    margin-bottom: 0px;
    padding-top: 2px;
    color: #999;
}
.multiselect__select {
    height: 36px;
    padding-top: 8px;
    width: 30px;
}
.multiselect__input {
    font-size: 14px;
    margin-bottom: 2px;
    padding-left: 0;
}
.multiselect--active .multiselect__tags {
    border-color: #3c8dbc;
}

/* Dropdown Options Styling - Proportional */
.multiselect__option {
    padding: 10px 12px; /* Standard padding */
    min-height: 36px;
    line-height: 16px;
    font-size: 14px; /* Standard font */
}
.multiselect__option:after {
    font-size: 12px;
    line-height: 36px;
    padding-right: 12px;
}
.multiselect__option--highlight {
    background: #3c8dbc;
    color: white;
}
.multiselect__option--highlight:after {
    background: #3c8dbc;
    color: white;
}
.multiselect__option--selected {
    background: #f3f3f3;
    color: #333;
    font-weight: bold;
}
.multiselect__option--selected:after {
    color: #999;
}
</style>
